/**
 * Gallery of the widget bar (#1822): pick a widget, see it, choose how it sits
 * in the bar. For a widget already there, the choice applies right away.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Search, X } from 'lucide-react'
import { useWidgetSettings } from '@/contexts/WidgetSettingsContext'
import { getActiveWidgetBarVariant, getWidgetBarVariants, type WidgetBarVariant } from '@/data/widgets'
import type { WidgetConfig, WidgetId } from '@/types/widget'
import { renderWidget } from '../WidgetRenderer'
import { WidgetPreviewProvider } from '../WidgetWrapper'
import { widgetCellClass } from './tile'

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'

export function WidgetGallery({
  available,
  initialSelected,
  onClose,
}: {
  available: WidgetId[]
  initialSelected?: WidgetId
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { widgetConfigs, enableWidget, updateWidgetSize, updateWidgetSettings } = useWidgetSettings()
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<WidgetId | undefined>(initialSelected ?? available[0])
  const [pendingVariant, setPendingVariant] = useState<WidgetBarVariant | null>(null)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return available.filter((id) =>
      `${t(`widgetBar.names.${id}`)} ${t(`widgetBar.descriptions.${id}`)}`.toLowerCase().includes(needle),
    )
  }, [available, query, t])

  const config = widgetConfigs.find((candidate) => candidate.id === selectedId)
  const inBar = Boolean(config?.enabled)
  const activeVariant = config && (inBar ? getActiveWidgetBarVariant(config) : pendingVariant ?? getWidgetBarVariants(config.id)[0])
  const previewConfig: WidgetConfig | undefined =
    config && activeVariant
      ? { ...config, size: activeVariant.size, settings: { ...config.settings, ...activeVariant.settings } }
      : undefined

  const applyVariant = (id: WidgetId, variant: WidgetBarVariant) => {
    updateWidgetSize(id, variant.size)
    if (variant.settings) updateWidgetSettings(id, variant.settings)
  }

  const pickVariant = (variant: WidgetBarVariant) => {
    if (!config) return
    if (inBar) applyVariant(config.id, variant)
    else setPendingVariant(variant)
  }

  const add = () => {
    if (!config || !activeVariant) return
    enableWidget(config.id)
    applyVariant(config.id, activeVariant)
    setPendingVariant(null)
  }

  return (
    <section
      role="dialog"
      aria-label={t('widgetBar.add')}
      data-widget-gallery
      className="fixed bottom-[128px] right-[456px] top-[72px] z-[13000] flex w-[min(920px,calc(100vw-520px))] flex-col overflow-hidden rounded-3xl border border-[var(--window-border-color)] bg-[var(--window-background)] text-[var(--text-color)] shadow-2xl"
    >
      <div className="flex items-center gap-4 border-b border-[var(--window-border-color)] px-6 py-5">
        <h2 className="flex-1 text-xl font-bold">{t('widgetBar.add')}</h2>
        <label className="flex h-10 w-[280px] items-center gap-2 rounded-[10px] border border-[var(--window-border-color)] px-3 text-[var(--text-secondary)]">
          <Search className="h-4 w-4 flex-shrink-0" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('widgetBar.search')}
            aria-label={t('widgetBar.search')}
            className="min-w-0 flex-1 bg-transparent text-sm text-[var(--text-color)] outline-none"
          />
        </label>
        <button
          type="button"
          aria-label={t('widgetBar.close')}
          onClick={onClose}
          className={`flex h-10 w-10 items-center justify-center rounded-full border border-[var(--window-border-color)] ${FOCUS_RING}`}
        >
          <X className="h-[18px] w-[18px]" aria-hidden />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
        {config && previewConfig && activeVariant && (
          <div className="flex gap-6 rounded-[20px] border border-[var(--theme-interactive-color)] p-5">
            <div className="flex w-[368px] flex-shrink-0 items-center justify-center rounded-2xl">
              <WidgetPreviewProvider>
                <div
                  className={`pointer-events-none grid grid-cols-2 ${previewConfig.size === 'small' ? 'w-[336px] justify-items-center [&>*]:col-span-2 [&>*]:w-[162px]' : 'w-[336px]'}`}
                  aria-hidden
                >
                  <div className={widgetCellClass(previewConfig.id, previewConfig.size)}>
                    {renderWidget(previewConfig.id, {
                      widgetId: previewConfig.id,
                      config: previewConfig,
                      onOpenApp: () => {},
                      staticPreview: true,
                    })}
                  </div>
                </div>
              </WidgetPreviewProvider>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="text-lg font-bold">{t(`widgetBar.names.${config.id}`)}</div>
              <div className="text-sm leading-relaxed text-[var(--text-secondary)]">
                {t(`widgetBar.descriptions.${config.id}`)}
              </div>
              <div role="group" aria-label={t('widgetBar.moved')} className="flex flex-wrap gap-2">
                {getWidgetBarVariants(config.id).map((variant) => {
                  const active = variant.id === activeVariant.id
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => pickVariant(variant)}
                      className={`h-9 min-w-16 rounded-full border px-3.5 text-[13px] font-medium ${FOCUS_RING} ${
                        active
                          ? 'border-transparent bg-[var(--theme-interactive-color)] text-[var(--theme-interactive-foreground)]'
                          : 'border-[var(--window-border-color)] text-[var(--text-color)]'
                      }`}
                    >
                      {t(`widgetBar.variants.${variant.id}`)}
                    </button>
                  )
                })}
              </div>
              {!inBar && (
                <button
                  type="button"
                  onClick={add}
                  className={`mt-auto h-11 self-start rounded-full bg-[var(--theme-interactive-color)] px-5 text-sm font-bold text-[var(--theme-interactive-foreground)] ${FOCUS_RING}`}
                >
                  {t('widgetBar.addToBar')}
                </button>
              )}
            </div>
          </div>
        )}

        {visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--text-secondary)]">{t('widgetBar.noResults')}</p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {visible.map((id) => (
              <button
                key={id}
                type="button"
                aria-pressed={id === selectedId}
                onClick={() => {
                  setSelectedId(id)
                  setPendingVariant(null)
                }}
                className={`flex min-h-[92px] flex-col gap-1.5 rounded-2xl border p-3.5 text-left ${FOCUS_RING} ${
                  id === selectedId ? 'border-[var(--theme-interactive-color)]' : 'border-[var(--window-border-color)]'
                }`}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="text-sm font-medium">{t(`widgetBar.names.${id}`)}</span>
                  {widgetConfigs.find((candidate) => candidate.id === id)?.enabled && (
                    <span className="flex items-center gap-1 whitespace-nowrap text-xs text-[var(--text-secondary)]">
                      <Check className="h-3 w-3" aria-hidden />
                      {t('widgetBar.inBar')}
                    </span>
                  )}
                </span>
                <span className="text-xs leading-snug text-[var(--text-secondary)]">
                  {t(`widgetBar.descriptions.${id}`)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
